/**
 * The subtitle track of a recording whose sound was transcribed (PRD-0115 SC-020): WebVTT made from
 * its turns, a cue per turn for its span with the voice that speaks it, so the player's own captions
 * control shows what was said over the picture — in full screen too — and the file can be saved.
 * Nothing is written into the picture. Pure.
 */
import type { TFunction } from 'i18next'
import type { VideoTranscriptTurn } from '@/services/api/video'

/** A turn's voice as the transcript and the subtitles name it: its number, or none. */
export function voiceLabel(voice: number, t: TFunction): string {
  return voice > 0
    ? t('documents.detail.video.transcript.voice', { voice })
    : t('documents.detail.video.transcript.unknownVoice')
}

/** A seek lands a few milliseconds before the time asked for: the turn clicked is still the one marked. */
const SEEK_SLACK_MS = 150

/** `75_500` → `00:01:15.500`, the timestamp form WebVTT takes. */
export function vttTime(ms: number): string {
  const total = Math.max(0, Math.round(ms))
  const hours = Math.floor(total / 3_600_000)
  const minutes = Math.floor((total % 3_600_000) / 60_000)
  const seconds = Math.floor((total % 60_000) / 1000)
  const pad = (value: number, width = 2) => String(value).padStart(width, '0')
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}.${pad(total % 1000, 3)}`
}

/** A cue's text: one line, with what WebVTT reads as markup escaped. */
function cueText(text: string): string {
  return text
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * The WebVTT file of *turns*, each cue prefixed with *voiceLabel* of its voice. A turn with no text
 * or no span is left out; a cue ends no earlier than a tenth of a second after it starts.
 */
export function toWebVtt(
  turns: readonly VideoTranscriptTurn[],
  voiceLabel: (voice: number) => string
): string {
  const cues = turns
    .filter(turn => turn.text.trim() && turn.end_ms >= turn.start_ms)
    .map(turn => {
      const end = Math.max(turn.end_ms, turn.start_ms + 100)
      return `${turn.index + 1}\n${vttTime(turn.start_ms)} --> ${vttTime(end)}\n${cueText(
        `${voiceLabel(turn.voice)}: ${turn.text}`
      )}`
    })
  return `${['WEBVTT', ...cues].join('\n\n')}\n`
}

/**
 * The place of the turn being said at *seconds*, or of the last one said before it; -1 before the first.
 * *turns* are in time order: a binary search, as it is asked on every tick of the player.
 */
export function turnAt(turns: readonly VideoTranscriptTurn[], seconds: number | null): number {
  if (seconds === null) return -1
  const ms = seconds * 1000 + SEEK_SLACK_MS
  let lo = 0
  let hi = turns.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (turns[mid].start_ms <= ms) lo = mid + 1
    else hi = mid
  }
  return lo - 1
}

/** The subtitle track the player offers under its captions control. */
export interface SubtitleTrack {
  /** An object URL of the WebVTT file, valid while the recording is open. */
  src: string
  label: string
  language: string | null
  /** What the file is saved as. */
  filename: string
}
